/**
 * @graphql cc
 */
import React, { useCallback, useRef } from 'react';

import { token } from '@atlaskit/tokens';
import { cssMap, cx } from '@atlaskit/css';
import { Flex, Box } from '@atlaskit/primitives/compiled';

import {
	useObjectCommentDispatchContext,
	useObjectCommentContext,
} from '@confluence/comment-context/entry-points/ObjectCommentContext.ts';
import { AddFooterCommentMode } from '@confluence/comments-panel-utils/entry-points/useCommentsPanel.ts';
import { AnalyticsSource } from '@confluence/comments-util/entry-points/analytics.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import {
	ADD_PAGE_COMMENT_EXPERIENCE,
	ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
	ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
	VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceStart } from '@confluence/experience-tracker/entry-points/component.ts';
import { startEditorExperiences } from '@confluence/experience-tracker/entry-points/startExperienceForRoute.ts';
import { getCommentsDataManagerExperimentAttributes } from '@confluence/comments-data-manager/entry-points/getCommentsDataManagerExperimentAttributes.ts';
import { componentWithFG } from '@confluence/feature-gate-component';
import { fg } from '@confluence/feature-gating';
import { LoadablePaint } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { ObjectAddCommentPrompt } from './ObjectAddCommentPrompt.tsx';
import { ObjectCommentPageReactions } from './ObjectCommentPageReactions.tsx';
import { ObjectAddCommentContainerLegacy } from './ObjectAddCommentContainerLegacy.tsx';

const FooterLabelsLoader = LoadablePaint({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-FooterLabels" */ '@confluence/labels-footer'))
			.FooterLabels,
});

const styles = cssMap({
	oldAddCommentContainerStyles: {
		position: 'sticky',
		bottom: token('space.0'),
		display: 'flex',
		width: '100%',
		padding: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('color.background.input'),
	},
	addCommentContainerStyles: {
		position: 'sticky',
		bottom: token('space.0'),
		display: 'flex',
		width: '100%',
		padding: token('space.200'),
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		backgroundColor: token('elevation.surface'),
	},
	addCommentClosedContainerStyles: {
		width: '100%',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		flexShrink: 0,
	},
	hideStyle: {
		display: 'none',
	},
	addCommentReactionsContainerStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	rightButtonContainerStyles: {
		alignItems: 'center',
	},
});

export const ObjectAddCommentContainerComponent = ({
	showReactions,
	showLabels,
	isOffline,
	optimisticReactions = false,
}: {
	showReactions: boolean;
	showLabels: boolean;
	isOffline?: boolean;
	optimisticReactions?: boolean;
}) => {
	const { edition } = useSessionData();
	const shouldAutofocusPromptRef = useRef<boolean>(false);
	const { expandCommentContainer, setFooterEditorEntry } = useObjectCommentDispatchContext();
	const { expanded } = useObjectCommentContext();

	const [contentId] = usePageContentId();

	const objectAddCommentContainerId = 'addCommentButton';

	const handleExpandCommentContainer = useCallback(() => {
		if (!fg('confluence_fe_add_page_comment_exp_coverage')) {
			startEditorExperiences(
				{
					name: ADD_PAGE_COMMENT_EXPERIENCE,
					attributes: {
						...getCommentsDataManagerExperimentAttributes(),
						editor: 'FABRIC',
						isSingleExperience: true,
						edition,
						source: AnalyticsSource.PAGE_FOOTER,
					},
				},
				{
					name: ADD_PAGE_COMMENT_LOAD_EXPERIENCE,
				},
				{
					name: ADD_PAGE_COMMENT_PUBLISH_EXPERIENCE,
				},
			);
		}

		expandCommentContainer({
			entryPoint: 'pageFooter',
		});
		setFooterEditorEntry(AddFooterCommentMode.FOOTER);
	}, [edition, expandCommentContainer, setFooterEditorEntry]);

	return (
		<>
			<Box
				id={objectAddCommentContainerId}
				xcss={cx(
					fg('confluence_frontend_content_wrapper') && styles.addCommentContainerStyles,
					!fg('confluence_frontend_content_wrapper') && styles.oldAddCommentContainerStyles,
				)}
				testId={objectAddCommentContainerId}
			>
				{!expanded && (
					<Box xcss={styles.addCommentClosedContainerStyles}>
						<ObjectAddCommentPrompt
							onClick={handleExpandCommentContainer}
							autofocusRef={shouldAutofocusPromptRef}
						/>
						<Flex
							gap="space.050"
							testId="objectAddCommentContainer-rightButtonContainer"
							xcss={styles.rightButtonContainerStyles}
						>
							{showLabels && fg('confluence_frontend_labels_in_footer') && (
								<>
									<ExperienceStart id={contentId} name={VIEW_PAGE_LABELS_IN_FOOTER_EXPERIENCE} />
									<FooterLabelsLoader />
								</>
							)}
							{showReactions && (
								<Box xcss={styles.addCommentReactionsContainerStyles}>
									<ObjectCommentPageReactions
										isOffline={isOffline}
										optimisticReactions={optimisticReactions}
									/>
								</Box>
							)}
						</Flex>
					</Box>
				)}
			</Box>
		</>
	);
};

// This FG lets us quick revert without causing a HOT
// Don't update the legacy component, it will be removed shortly
export const ObjectAddCommentContainer = withErrorBoundary({
	attribution: Attribution.COMMENTS,
})(
	componentWithFG(
		'confluence_comment_object_add_comment_refactor',
		ObjectAddCommentContainerComponent,
		ObjectAddCommentContainerLegacy,
	),
);
